import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import App from './App'
import { useAuthStore } from './stores/useAuthStore'
import './assets/styles/main.css'
import 'maplibre-gl/dist/maplibre-gl.css'

// Détecte les erreurs WebGL pour les enrichir et abaisser leur sévérité (warning)
// quand un fallback UI est en place. Les attributs MapLibre figurent dans `requestedAttributes`.
function isWebGLEvent(event) {
  const type = event?.exception?.values?.[0]?.type
  const value = event?.exception?.values?.[0]?.value || event?.message || ''
  if (type === 'webglcontextcreationerror') return true
  return /failed to initialize webgl|webgl.*context/i.test(value)
}

// Sentry — monitoring erreurs en mode minimal (sans PII ni replays).
// Les fonctionnalités avancées (PII, replays, tracing) sont activées
// uniquement après consentement RGPD via consent.js → upgradeSentry().
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_ENV || 'development',
    // Sans `release`, tous les évènements se confondent et une issue marquée
    // « résolue » ne peut pas être rouverte automatiquement à la régression :
    // c'est ainsi que le 503 du rate limiting a pu revenir sans être vu.
    // Injectée au build par `deploy.sh` (cf. docker/nginx/Dockerfile).
    release: import.meta.env.VITE_APP_VERSION || undefined,
    sendDefaultPii: false,
    integrations: [],
    tracesSampleRate: 0,
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.?bonurba\.fr/],
    enableLogs: true,
    beforeSend(event) {
      if (isWebGLEvent(event)) {
        // Si on a déjà émis un message structuré côté util/webgl.js (tag présent),
        // on laisse passer tel quel. Sinon on déclasse l'événement brut en warning :
        // un fallback UI est garanti par <WebGLFallback /> dans MapContainer.
        if (!event.tags?.['webgl.failure_reason']) {
          event.level = 'warning'
          event.tags = { ...(event.tags || {}), 'webgl.failure_reason': 'unhandled' }
        }
      }
      return event
    },
  })
}

// Fallback affiché en cas de crash React (erreur de rendu)
function ErrorFallback() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Inter, sans-serif', color: '#333', padding: '2rem', textAlign: 'center' }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Une erreur inattendue est survenue</h1>
      <p style={{ marginBottom: '1.5rem', color: '#666' }}>Notre équipe a été notifiée. Veuillez recharger la page.</p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '0.75rem 1.5rem', backgroundColor: '#529371', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '1rem' }}
      >
        Recharger la page
      </button>
    </div>
  )
}

// Restaurer la session si un refresh token est persisté
useAuthStore.getState().initializeAuth()

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <App />
      </Sentry.ErrorBoundary>
    </BrowserRouter>
  </React.StrictMode>
)
